import { Link } from "@mui/material";

const OnlineTools = () => {
  return (
    <div className="container--blog_post_page_wrapper">
      <section className="container--blog_post">
        <div className="container--post_content">
          <p className="text--post_paragraph">
            Careful and thorough research requires time, information,
            collaboration, and organization. There are a myriad of online
            resources available to students and professionals, allowing users to
            access studies, papers, libraries, and articles while keeping
            accumulated data easily available to all team members in one
            convenient location. Below are five of the most useful online tools,
            each offering unique features that will help make research-gathering
            and analysis easier and more efficient.
          </p>
          <h2 className="header header--page_title_h2_left">1. Mendeley</h2>
          <p className="text--post_paragraph">
            Mendeley has over 100 million articles available and provides
            products and services for academic researchers. Their Reference
            Manager, a free web and desktop application, allows the user to
            create references, citations, and bibliographies in any individual
            format. The user can access all stored library information, insert
            bibliographies and references into a Word document, annotate PDF
            files, and collaborate with fellow users. This streamlines the
            research process, keeping all needed reference material in one
            place. According to their website, their users have “uploaded more
            than 400 million citations and documents into their Mendeley
            accounts.”{" "}
            <Link
              href="https://www.elsevier.com/connect/archive/victor-hennings-brief-guide-to-mendeley"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read Victor Henning's brief guide to Mendeley on Elsevier Connect"
            >
              Mendeley Blog - FAQ
            </Link>{" "}
            Additionally, Mendeley provides sharing opportunities. This allows
            researchers to share ideas, collaborate, and participate in
            discussions. This application also provides the opportunity to
            search over 677,023 job listings{" "}
            <Link
              href="https://www.mendeley.com/careers/"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Explore health jobs on Mendeley Careers"
            >
              health jobs
            </Link>{" "}
            by field or region. Mendeley also has Featured Job and Featured
            Employer sections, and users can upload their resume/CV to be
            matched with potential employers. Mendeley was developed using
            feedback from actual researchers, improving many practical aspects
            and overall user experience.
          </p>
          <h2 className="header header--page_title_h2_left">
            2. Google Scholar
          </h2>
          <p className="text--post_paragraph">
            Google Scholar is a free academic search engine that provides
            extensive stores of both full text and metadata. This allows users
            to search a wide range of resources such as journals, white papers,
            patents, and articles from a large number of academic libraries,
            increasing the chances of finding relevant and targeted research
            information.{" "}
            <Link
              href="https://scanmarker.com/2019/09/25/the-5-best-tools-for-researchers/"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Read about the best tools for researchers on Scanmarker.com"
            >
              (Scanmarker.com)
            </Link>{" "}
            A useful feature is the Alert, which will let the user know if a new
            article or research has been released in their designated field,
            keeping them informed of current developments. Even among multiple
            libraries, Google Scholar can manage papers, providing the current
            citation number and version of any article being read. This will
            help the user know if any other researcher has cited that work.
          </p>
          <p className="text--post_paragraph">
            Google Scholar also has a vast legal database of U.S Cases,
            providing information on state appellate court cases, federal
            districts, tax, and bankruptcy cases, and US Supreme Court cases
            dating back to 1791. Google Scholar ranks results with a combined
            ranking algorithm “in a way researchers do, weighing the full text
            of each article, the author, the publication in which the article
            appears, and how often the piece has been cited in other scholarly
            literature.”{" "}
            <Link
              href="https://scholar.google.com/intl/en/scholar/about.html"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Learn more about Google Scholar's ranking algorithm on their official site"
            >
              (Google Scholar)
            </Link>
          </p>
          <p className="text--post_paragraph">
            A function called “Google Scholar Profiles” allows authors to
            highlight their scholarly research and makes it possible to discern
            if other researchers are citing their articles, graph their
            citations over time, and calculate citation metrics.{" "}
            <Link
              href="https://scholar.google.com/intl/en/scholar/citations.html"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Discover more about Google Scholar Profiles and citation metrics on Google Scholar"
            >
              (Google Scholar)
            </Link>{" "}
            Users can create an optional Public Profile, which will ensure that
            the author’s name appears when an Internet search is performed. For
            example, a search of the name “Albert Einstein” will provide the
            searcher a list of web pages, PDF files, and books listing Einstein
            as an author, as well as how many others have cited these resources
            in their own work.
          </p>
          <h2 className="header header--page_title_h2_left">3. TeammateMe</h2>
          <p className="text--post_paragraph">
            <Link
              href="https://www.teammateme.com"
              target="_blank"
              rel="noreferrer noopener dofollow"
              aria-label="Visit TeammateMe for online team-building and research collaboration"
            >
              Teammate<span className="makeGreen">Me</span>
            </Link>{" "}
            is an exciting online team-building tool that helps researchers and
            scholars connect with skilled professionals and find research
            partners and co-authors quickly and efficiently. The aggregated
            rating scores and anonymous reviews help researchers find
            appropriate partners for their research projects and papers. After
            assembling a qualified and reliable team, TeammateMe users have the
            option to converse in group chats, send direct messages to specific
            members, and provide anonymous feedback, all in one convenient,
            easy-to-use site. With TeammateMe, users can feel confident in their
            team and accomplish their research goals.
          </p>
          <p className="text--post_paragraph">
            Users find TeammateMe effective and easy-to-use. As one user
            proclaimed, “TeammateMe is a great platform to build a team. I
            signed up and started a new team in under a minute, and I was able
            to easily search and reach out to potential teammates.”
          </p>
          <h2 className="header header--page_title_h2_left">4. Zotero</h2>
          <p className="text--post_paragraph">
            <Link
              href="https://www.zotero.org/"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Explore Zotero, a personal research assistant and referencing tool"
            >
              Zotero
            </Link>{" "}
            is a free, open-source researching tool to find and use resources
            that can be integrated into a browser. As a "personal research
            assistant," Zotero technology can locate Internet content
            specifically tailored for each user, storing that content into a
            fully searchable personal library that can be accessed at any time.
            It also has over 9,000 citation styles, giving it the ability to
            create references and bibliographies for almost any style guide or
            publication. This tool is particularly helpful if one is using a new
            referencing system as it keeps the bibliography and references
            consistent. Zotero also organizes information based on the user’s
            preferences, can create saved searches that automatically populate
            with relevant data, and is able to sync information across devices,
            ensuring the user has the most up-to-date information at their
            fingertips.
          </p>
          <p className="text--post_paragraph">
            Zotero also provides excellent customer support. Instead of a
            Customer Service team, users are able to reach out to a developer
            and receive a timely response. New features are added regularly, and
            any bugs are generally fixed within one to two days. Zotero forums
            allow customers to participate in discussions that directly
            influence the development of future software applications and
            features.
          </p>
          <h2 className="header header--page_title_h2_left">5. RefWorks</h2>
          <p className="text--post_paragraph">
            Last but not least,{" "}
            <Link
              href="https://refworks.proquest.com/"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Visit RefWorks for web-based bibliography and database management"
            >
              RefWorks
            </Link>{" "}
            is a web-based bibliography and database manager that allows
            researchers to use references from text files, other online
            databases, or other sources, which the user can then use to
            automatically format papers and bibliographies quickly and
            effortlessly. RefWorks can also import citations directly from
            library databases as well as Google Scholar. RefWorks has a “drag
            and drop” option and document recognition, which makes it simple to
            upload documents and data into the user’s library. Additionally, the
            “Save to RefWorks” selection allows research gathering from other
            websites with one simple button function.
          </p>
          <p className="text--post_paragraph">
            Collaboration is streamlined on RefWorks, with all team members
            receiving full access to resources, as well as interacting,
            commenting, and annotating text in real-time. Bibliographies and
            citations can be created quickly, and there is a database of
            pre-built citation styles. Users may also request new styles,
            customize, or create original ones with the citation style editor.{" "}
            <Link
              href="https://refworks.proquest.com/researcher/"
              target="_blank"
              rel="noreferrer noopener nofollow sponsored"
              aria-label="Learn more about RefWorks for researchers on ProQuest"
            >
              (RefWorks)
            </Link>
          </p>
          <h2 className="header header--page_title_h2_left">
            Conclusion - Work Smarter, Not Harder
          </h2>
          <p className="text--post_paragraph">
            From quantitative to qualitative research and from solo analysis to
            synergy, one of the most essential elements for any research project
            is having the online tools to locate, organize, cite, and format
            information. The proper research application can be a vital,
            time-saving resource for research-gathering and should be utilized
            for optimal productivity. By implementing technology into academic
            research, applications like TeammateMe can help the user reach
            goals, gather quality information, stay organized, and collaborate
            effectively and efficiently.
          </p>
        </div>
        <hr className="hr--form_break" />
      </section>
    </div>
  );
};

export default OnlineTools;
